// client/src/components/ClassesTab.jsx
// "כיתות" tab on CampDetailPage (wave-10). Manage the camp's classes —
// each with a homeroom teacher + notes. Students reference a class by name
// (the student form offers these as a dropdown), and archiving a class is
// blocked while students are still assigned to it.

import { useMemo, useState } from 'react';
import ListToolbar from './ListToolbar.jsx';
import { matchesText, sortRows } from '../lib/listTools.js';
import {
  useClasses,
  useCreateClass,
  useUpdateClass,
  useDeleteClass,
} from '../hooks/useClasses.js';
import { ApiError } from '../lib/api.js';
import Spinner from './Spinner.jsx';
import Modal from './Modal.jsx';
import ClassForm from './ClassForm.jsx';
import {
  AcademicCapIcon,
  UserGroupIcon,
  PencilSquareIcon,
  ArchiveBoxXMarkIcon,
} from './Icons.jsx';

export default function ClassesTab({ campId }) {
  const classesQ = useClasses(campId);
  const createMutation = useCreateClass(campId);
  const updateMutation = useUpdateClass(campId);
  const deleteMutation = useDeleteClass(campId);

  const [creating, setCreating] = useState(false);
  const [createDirty, setCreateDirty] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editDirty, setEditDirty] = useState(false);
  const [removing, setRemoving] = useState(null); // class object
  const [removeError, setRemoveError] = useState(null);

  const classes = classesQ.data ?? [];
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const shown = useMemo(
    () =>
      sortRows(
        classes.filter((c) => matchesText(c, q, ['name', 'teacher', 'notes'])),
        sort,
        {
          name: (c) => c.name,
          teacher: (c) => c.teacher,
          studentCount: (c) => c.studentCount ?? 0,
        }
      ),
    [classes, q, sort]
  );

  return (
    <div className="space-y-4">
      <ListToolbar
        search={{ value: q, onChange: setQ, placeholder: 'חיפוש כיתה או מורה...' }}
        sort={{
          value: sort,
          onChange: setSort,
          options: [
            { key: 'name', label: 'שם הכיתה' },
            { key: 'teacher', label: 'מורה' },
            { key: 'studentCount', label: 'מספר תלמידים' },
          ],
        }}
        count={{ shown: shown.length, total: classes.length, noun: 'כיתות' }}
      >
        <button type="button" className="btn-pop" onClick={() => setCreating(true)}>
          <PlusIcon className="w-4 h-4" />
          כיתה חדשה
        </button>
      </ListToolbar>

      {classesQ.isLoading && !classesQ.data ? (
        <Spinner label="טוען כיתות..." />
      ) : classesQ.isError ? (
        <div className="card-danger p-4 text-joy-900 text-sm">
          שגיאה: {classesQ.error.message}
        </div>
      ) : classes.length === 0 ? (
        <EmptyState onCreate={() => setCreating(true)} />
      ) : shown.length === 0 ? (
        <div className="card p-8 text-center text-sm text-ink-500">לא נמצאו כיתות מתאימות לחיפוש.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((c) => (
            <ClassCard
              key={c.id}
              cls={c}
              onEdit={() => setEditing(c)}
              onRemove={() => {
                setRemoveError(null);
                setRemoving(c);
              }}
            />
          ))}
        </div>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        isDirty={createDirty}
        title="כיתה חדשה"
        maxWidth="max-w-md"
      >
        <ClassForm
          submitLabel="צור כיתה"
          onDirtyChange={setCreateDirty}
          onCancel={() => setCreating(false)}
          onSubmit={async (body) => {
            await createMutation.mutateAsync(body);
            setCreating(false);
          }}
        />
      </Modal>

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        isDirty={editDirty}
        title="עריכת כיתה"
        maxWidth="max-w-md"
      >
        {editing && (
          <ClassForm
            initial={editing}
            submitLabel="שמור שינויים"
            onDirtyChange={setEditDirty}
            onCancel={() => setEditing(null)}
            onSubmit={async (patch) => {
              await updateMutation.mutateAsync({ classId: editing.id, patch });
              setEditing(null);
            }}
          />
        )}
      </Modal>

      <Modal open={!!removing} onClose={() => setRemoving(null)} title="ארכוב כיתה">
        {removing && (
          <>
            <p className="text-sm text-ink-700">
              להעביר את כיתה <strong>{removing.name}</strong> לארכיון?
            </p>
            {removing.studentCount > 0 && (
              <p className="mt-2 text-sm text-clay-700">
                שימו לב: יש {removing.studentCount} תלמידים המשויכים לכיתה זו. צריך
                לשייך אותם לכיתה אחרת (או להסיר את השיוך) לפני הארכוב.
              </p>
            )}
            {removeError && (
              <div className="mt-3 card-danger p-3 text-joy-900 text-sm">{removeError}</div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn-secondary" onClick={() => setRemoving(null)}>
                ביטול
              </button>
              <button
                type="button"
                className="btn-danger"
                disabled={deleteMutation.isPending}
                onClick={async () => {
                  try {
                    await deleteMutation.mutateAsync(removing.id);
                    setRemoving(null);
                  } catch (err) {
                    if (err instanceof ApiError && err.message === 'class_not_empty') {
                      setRemoveError(
                        'לא ניתן לארכב כיתה עם תלמידים משויכים. שייך אותם לכיתה אחרת קודם.'
                      );
                    } else {
                      setRemoveError(`שגיאה: ${err.message || 'לא צפויה'}`);
                    }
                  }
                }}
              >
                {deleteMutation.isPending ? 'מארכב...' : 'כן, ארכב'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

function ClassCard({ cls, onEdit, onRemove }) {
  return (
    <div className="card p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 rounded-2xl bg-brand-100 text-brand-700 grid place-items-center shrink-0">
            <AcademicCapIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="font-bold text-ink-900 text-lg leading-tight">{cls.name}</div>
            <div className="text-xs text-ink-500 num">{cls.studentCount} תלמידים</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <IconAction label="ערוך" tone="brand" onClick={onEdit} Icon={PencilSquareIcon} />
          <IconAction label="ארכב" tone="joy" onClick={onRemove} Icon={ArchiveBoxXMarkIcon} />
        </div>
      </div>

      <div className="text-sm space-y-1">
        <div className="flex items-center gap-2 text-ink-700">
          <UserGroupIcon className="w-4 h-4 text-ink-400 shrink-0" />
          {cls.teacher ? (
            <span>{cls.teacher}</span>
          ) : (
            <span className="text-ink-400">ללא מורה משויך</span>
          )}
        </div>
        {cls.notes && <p className="text-ink-600 whitespace-pre-wrap">{cls.notes}</p>}
      </div>
    </div>
  );
}

const ICON_ACTION_TONE = {
  brand: 'text-ink-500 hover:text-brand-700 hover:bg-brand-50',
  joy: 'text-ink-500 hover:text-joy-700 hover:bg-joy-50',
};

function IconAction({ label, tone, onClick, Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-9 h-9 rounded-xl grid place-items-center transition-colors ${ICON_ACTION_TONE[tone]}`}
      aria-label={label}
      title={label}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="card-pop p-10 sm:p-14 text-center max-w-xl mx-auto">
      <div className="mx-auto w-16 h-16 rounded-3xl bg-brand-gradient text-white grid place-items-center mb-4 shadow-pop">
        <AcademicCapIcon className="w-8 h-8" />
      </div>
      <h2 className="text-lg font-bold text-ink-900">אין כיתות עדיין</h2>
      <p className="mt-2 text-muted">
        צור כיתות כדי לשייך אליהן תלמידים ולהפיק דוחות לפי כיתה.
      </p>
      <button type="button" className="btn-pop btn-lg mt-5" onClick={onCreate}>
        <PlusIcon className="w-5 h-5" />
        כיתה ראשונה
      </button>
    </div>
  );
}

function PlusIcon({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
    </svg>
  );
}
